import {getActiveSectionText} from '../../../../../utils';
import {
  BaseNodeFields,
  getBranchFieldPath,
  getNetworkFieldPath,
  getSubBranchFieldPath,
  NetworkNodeFieldPath,
  NetworkNodeFormValues,
} from '../types';

export type NodeSourceOption = {
  title: string;
  value: NetworkNodeFieldPath;
};

export const formatNodeSourceTitle = (
  name: string,
  fieldPath: NetworkNodeFieldPath,
) => {
  const displayName = name?.trim() || 'ללא שם';
  const level = getActiveSectionText(fieldPath.toString());
  return level ? `${level}: ${displayName}` : displayName;
};

export const getNetworkIndexFromPath = (
  currentPath: NetworkNodeFieldPath,
): number | null => {
  const parts = currentPath.split('.');
  if (parts[0] !== 'network' || parts.length < 2) return null;

  const networkIndex = Number.parseInt(parts[1], 10);
  return Number.isNaN(networkIndex) ? null : networkIndex;
};

const tryAddSourceOption = <T>(
  options: NodeSourceOption[],
  fieldPath: NetworkNodeFieldPath,
  currentPath: NetworkNodeFieldPath,
  name: string,
  items: T[],
  hasValidItems: (items: T[]) => boolean,
) => {
  if (fieldPath === currentPath) return;
  if (!hasValidItems(items)) return;

  options.push({
    title: formatNodeSourceTitle(name, fieldPath),
    value: fieldPath,
  });
};

export function collectNodeSourceOptions<T>(
  network: NetworkNodeFormValues[],
  currentPath: NetworkNodeFieldPath,
  getItems: (node: BaseNodeFields) => T[],
  hasValidItems: (items: T[]) => boolean,
): NodeSourceOption[] {
  const options: NodeSourceOption[] = [];
  const networkIndex = getNetworkIndexFromPath(currentPath);
  if (networkIndex === null) return options;

  const networkNode = network[networkIndex];
  if (!networkNode) return options;

  tryAddSourceOption(
    options,
    getNetworkFieldPath(networkIndex),
    currentPath,
    networkNode.name,
    getItems(networkNode),
    hasValidItems,
  );

  networkNode.branches.forEach((branch, j) => {
    tryAddSourceOption(
      options,
      getBranchFieldPath(networkIndex, j),
      currentPath,
      branch.name,
      getItems(branch),
      hasValidItems,
    );

    (branch.subBranches ?? []).forEach((subBranch, k) => {
      tryAddSourceOption(
        options,
        getSubBranchFieldPath(networkIndex, j, k),
        currentPath,
        subBranch.name,
        getItems(subBranch),
        hasValidItems,
      );
    });
  });

  return options;
}
